import { ApiError } from '../api';

/** Schnittstelle der Betreiber-Konsole (admin.<Plattform>). Sitzung per Cookie, Änderungen mit Schutz-Header. */

export interface OperatorIdentity {
  operator: { email: string; name: string | null };
}

export interface ProjectUser {
  email: string;
  name: string | null;
  role: string;
  lastLoginAt: string | null;
}

export interface ProjectOverview {
  id: string;
  slug: string;
  name: string;
  customDomain: string | null;
  timezone: string;
  createdAt: string;
  users: ProjectUser[];
  webinars: number;
  activeWebinars: number;
  registrations: number;
  lastRegistrationAt: string | null;
}

async function call<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`/api/operator${path}`, {
    method: init.method ?? 'GET',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'fetch',
      ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  if (!res.ok) {
    let code = String(res.status);
    let message = '';
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      code = body.error ?? code;
      message = body.message ?? '';
    } catch {
      /* kein JSON */
    }
    throw new ApiError(res.status, message || code);
  }
  return (await res.json()) as T;
}

export const operatorApi = {
  me: () => call<OperatorIdentity>('/me'),
  login: (email: string, password: string) => call<OperatorIdentity>('/login', { method: 'POST', body: { email, password } }),
  logout: () => call<{ ok: true }>('/logout', { method: 'POST' }),
  changePassword: (currentPassword: string, newPassword: string) => call<{ ok: true }>('/password', { method: 'POST', body: { currentPassword, newPassword } }),
  listProjects: () => call<{ projects: ProjectOverview[] }>('/projects'),
  createProject: (input: { slug: string; name: string; email?: string; adminName?: string }) => call<{ id: string; password: string | null }>('/projects', { method: 'POST', body: input }),
  updateProject: (id: string, patch: { name?: string; slug?: string; customDomain?: string | null }) => call<{ ok: true }>(`/projects/${id}`, { method: 'PATCH', body: patch }),
  addUser: (id: string, email: string, name: string) => call<{ password: string; reset: boolean }>(`/projects/${id}/users`, { method: 'POST', body: { email, ...(name ? { name } : {}) } }),
};

export function errorText(error: unknown, fallback = 'Das hat nicht geklappt. Bitte versuch es noch einmal.'): string {
  if (error instanceof ApiError && error.code && !/^\d+$/.test(error.code)) return error.code;
  return fallback;
}
